import { Fragment, useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Page, Card, EmptyState, StatusPill, TH, TD } from "@/components/polaris";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { RefreshCw, AlertTriangle, XCircle, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const cancelJob = async (id) => {
    try {
      await api.post(`/jobs/${id}/cancel`);
      toast.success("Cancelling — the job will stop shortly and partial data is discarded.");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <Layout>
      <Page title="Ingestion jobs" subtitle="Parsing, chunking and indexing status, with parsing warnings." width="max-w-6xl"
        actions={<Button variant="outline" size="sm" className="gap-1.5 bg-white border-[#C9CCCF] hover:bg-[#F1F2F4] rounded-lg" onClick={load} data-testid="refresh-jobs"><RefreshCw className="h-4 w-4" /> Refresh</Button>}>
        {jobs.length === 0 ? (
          <EmptyState icon={Activity} data-testid="empty-jobs">No ingestion jobs yet.</EmptyState>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr>
                    <th className={TH}>Status</th><th className={TH}>Document</th><th className={TH}>Progress</th>
                    <th className={TH}>Updated</th><th className={`${TH} text-right`}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => {
                    const cancellable = (j.status === "running" || j.status === "queued") && !j.cancel_requested;
                    const status = j.cancel_requested && (j.status === "running" || j.status === "queued") ? "cancelling" : j.status;
                    const st = j.stats || {};
                    const hasWarnings = j.warnings?.length > 0;
                    return (
                      <Fragment key={j.id}>
                        <tr className="hover:bg-[#FAFAFB] transition-colors" data-testid={`job-${j.id}`}>
                          <td className={`${TD} whitespace-nowrap ${hasWarnings ? "border-b-0" : ""}`}><StatusPill status={status} data-testid={`job-status-${j.id}`} /></td>
                          <td className={`${TD} font-medium text-[#202223] ${hasWarnings ? "border-b-0" : ""}`}>{j.document_title}</td>
                          <td className={`${TD} min-w-[220px] ${hasWarnings ? "border-b-0" : ""}`}>
                            {j.status === "running" && st.pages_processed != null && (
                              <div className="mb-1.5" data-testid="job-progress">
                                <div className="flex justify-between text-xs text-[#6D7175] mb-1">
                                  <span>{st.pages_processed}{st.total_pages ? ` / ${st.total_pages}` : ""} pages</span>
                                  <span>{st.chunk_count || 0} chunks</span>
                                </div>
                                <div className="h-1.5 bg-[#E3E5E7] rounded-full overflow-hidden">
                                  <div className="h-full bg-[#1E7FC2] rounded-full transition-[width] duration-500"
                                    style={{ width: st.total_pages ? `${Math.round((st.pages_processed / st.total_pages) * 100)}%` : "40%" }} />
                                </div>
                              </div>
                            )}
                            {Object.keys(st).length > 0 && (
                              <div className="flex gap-x-3 gap-y-0.5 text-xs text-[#6D7175] flex-wrap">
                                {st.total_pages != null && <span>{st.total_pages} pages</span>}
                                {st.chunk_count != null && <span>{st.chunk_count} chunks</span>}
                                {st.table_chunks != null && <span>{st.table_chunks} tables</span>}
                                {st.empty_pages != null && <span>{st.empty_pages} low-text pages</span>}
                              </div>
                            )}
                          </td>
                          <td className={`${TD} whitespace-nowrap text-xs text-[#6D7175] ${hasWarnings ? "border-b-0" : ""}`}>{j.updated_at?.slice(0, 19).replace("T", " ")}</td>
                          <td className={`${TD} text-right ${hasWarnings ? "border-b-0" : ""}`}>
                            {cancellable && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="h-8 gap-1 text-xs rounded-lg text-[#7A1408] border-[#F8B4B4] bg-white hover:bg-[#FDE8E8] hover:text-[#7A1408]" data-testid={`cancel-job-${j.id}`}>
                                    <XCircle className="h-3.5 w-3.5" /> Cancel
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-testid="cancel-job-dialog">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Cancel this ingestion job?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      “{j.document_title}” will stop processing and any partially indexed pages
                                      will be discarded so they don’t affect search results. You can run it again later with Reprocess.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="cancel-job-dismiss">Keep running</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => cancelJob(j.id)} data-testid={`confirm-cancel-job-${j.id}`}
                                      className="bg-[#D72C0D] text-white hover:bg-[#BC2200]">
                                      Cancel job
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </td>
                        </tr>
                        {hasWarnings && (
                          <tr>
                            <td colSpan={5} className="px-4 pb-3 border-b border-[#EDEEF0]">
                              <div className="space-y-1.5">
                                {j.warnings.map((w, i) => (
                                  <div key={i} className="flex gap-2 items-start text-sm border border-[#FFD6A5] bg-[#FFF8E6] text-[#4A2C00] rounded-lg px-3 py-2" data-testid="job-warning">
                                    <AlertTriangle className="h-4 w-4 text-[#B25900] mt-0.5 shrink-0" /><span>{w}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Page>
    </Layout>
  );
}
